import HTMLPlayer from './html';

// import { C2PA, ValidationResult, C2PAValidationError } from '@ravnur-inc/c2pa';
import {
   findIndex,
   existy,
   createElement,
   first,
   isArray,
   isObject,
   head,
   extend,
   find
   // isEqualBuffer
} from '../utils';

const AUTO_LEVEL = { name: 'Auto' };
const HLS_OPTIONS = {
   playlistLoadPolicy: {
      default: {
         maxLoadTimeMs: 60000,
         timeoutRetry: {
            maxNumRetry: 10,
            retryDelayMs: 0,
            maxRetryDelayMs: 0
         },
         errorRetry: {
            maxNumRetry: 10,
            retryDelayMs: 3000,
            maxRetryDelayMs: 8000
         }
      }
   }
};

export default class HLSPlayer extends HTMLPlayer {
   constructor(options, state) {
      super(options, state);
      this.logger.debug('hls:player:created');
   }

   load({ src }) {
      src = isArray(src) ? head(src) : src;
      src = isObject(src) ? src.src : src;
      this.logger.debug('hls:player:loading', src);

      if (existy(window.Hls)) {
         this._attachMedia(src);
      } else {
         this._loadLibraryAndAttachMedia(src);
      }
   }

   _loadLibraryAndAttachMedia(src) {
      // use requiresjs if we can
      if (typeof window.define === 'function' && window.define.amd) {
         window.require.config({
            paths: {
               hlsjs: this.options.hlsjsURL
            }
         });

         window.require(['hlsjs'], hls => {
            window.Hls = hls;
            this._attachMedia(src);
         });
      } else {
         const script = createElement('script', { src: this.options.hlsjsURL });
         const firstScriptTag = first(document.getElementsByTagName('script'));
         script.onload = script.onreadystatechange = () => this._attachMedia(src);

         if (!firstScriptTag.parentNode) return;

         firstScriptTag.parentNode.insertBefore(script, firstScriptTag);
      }
   }

   _attachMedia(src) {
      const Hls = window.Hls;
      if (Hls.isSupported()) {
         this.hls = new Hls(extend(HLS_OPTIONS, this.options.hls));

         // if (this.options.showC2PA) {
         //    this.c2pa = new C2PA((status, data) => {
         //       switch (status) {
         //       case ValidationResult.Complete:
         //          this.bus.emit('c2painit', { status, data });
         //          break;
         //       case ValidationResult.NoData:
         //          this.bus.emit('c2painit', { status, data });
         //          break;
         //       case ValidationResult.Failed:
         //          this.bus.emit('c2painit', { status, data });
         //          if (!(data instanceof C2PAValidationError)) {
         //             this.bus.emit('c2paexception');
         //          }
         //          break;
         //       default:
         //          console.debug(`[HLS] Unrecognized status ${status}`);
         //       }
         //    });

         //    // The video init segment is parsing
         //    this.hls.on(Hls.Events.FRAG_PARSING_INIT_SEGMENT, (name, data) => {
         //       console.debug(`[HLS] Executed ${name} handler, data: ${data}`);

         //       this.c2pa.initSegment(data);
         //    });

         //    // The video segment is appending to buffer
         //    this.hls.on(Hls.Events.BUFFER_APPENDING, (name, data) => {
         //       console.debug(`[HLS] Executed ${name} handler, data: ${data}`);

         //       // Check whether buffered segment is init segment
         //       if (isEqualBuffer(data.data, data.frag.initSegment.data)) {
         //          return;
         //       }

         //       // TODO: c2pa.validateSegment should define own data model, not just accept hls event data
         //       data.data1 = data.data;
         //       this.c2pa.validateSegment(data, validationResult => {
         //          this.bus.emit('c2pafragment', validationResult);
         //       });
         //    });
         // }

         this.hls.loadSource(src);
         this.hls.attachMedia(this.$el);

         this.hls.on(Hls.Events.ERROR, (a, data) => {
            this.bus.emit('error', data);
            this.logger.error(data);
         });

         this.hls.on(Hls.Events.AUDIO_TRACK_SWITCHED, () => {
            this.bus.emit('audiotrackswitched');
         });

         this.hls.on(Hls.Events.MANIFEST_PARSED, (a, data) => {
            if (!this.options.showSubtitles) {
               this.hls.subtitleDisplay = false;
            }

            if (this.options.qualityLevel) {
               const h = parseInt(this.options.qualityLevel, 10);
               if (h) {
                  const level = find(data.levels, l => l.height === h);
                  if (level) {
                     this.setLevel(level);
                  }
               }
            }

            if (this.options.startLevel) {
               const h = parseInt(this.options.startLevel, 10);
               if (h) {
                  const idx = findIndex(data.levels, l => l.height === h);
                  this.hls.startLevel = idx;
               }
            }
         });

         this.hls.on(Hls.Events.BUFFER_CREATED, () => {
            if (this.options.isLive) {
               this.goToLive();
            }
         });

         // Encryption. Token should be passed as an option or as a query parameter
         if (this.options.aesToken) {
            let descriptionUrl;

            this.hls.config.xhrSetup = (xhr, url) => {
               xhr.addEventListener('load', () => {
                  if (typeof xhr.response === 'string' && xhr.response.indexOf('#EXTM3U') >= 0) {
                     const lines = xhr.response.split('\n');
                     const keyLine = lines.find(line => line.indexOf('#EXT-X-KEY') === 0);
                     const keyUrl = keyLine?.split('URI="')[1].split('"')[0];
                     const urlBase = 'https://' + url.split('/')[2];
                     const finalUrl = urlBase + keyUrl;

                     if (keyUrl) {
                        descriptionUrl = finalUrl;
                     }
                  }
               });

               if (url === descriptionUrl) {
                  xhr.setRequestHeader('Authorization', 'Bearer ' + this.options.aesToken);
               }
            };
         } else {
            // append all query parameters to the subsequent requests
            const query = src.split('?')[1];

            if (query) {
               this.hls.config.xhrSetup = (xhr, url) => {
                  xhr.open('GET', url + `?${query}`, true);
               };
            }
         }
      } else {
         this.logger.error('HLS is not supported for this browser');
      }
   }

   goToLive() {
      if (!this.hls.media) {
         return;
      }

      this.logger.debug('hls:player:live');
      this.hls.media.currentTime = this.hls.liveSyncPosition || this.hls.media.duration - 10;
   }

   isLive() {
      if (this.hls?.liveSyncPosition) {
         return this.hls?.media.currentTime >= (this.hls?.liveSyncPosition - 10);
      }

      return this.hls?.media.currentTime >= (this.hls?.media.duration - 10); // Default to 10s from duration
   }

   getLevels() {
      return [AUTO_LEVEL].concat(this.hls.levels);
   }

   setLevel(level) {
      const idx = findIndex(this.hls.levels, level);
      this.hls.currentLevel = idx;
   }

   getLevel() {
      return this.hls.levels[this.hls.manualLevel] || AUTO_LEVEL;
   }

   isMultiQuality() {
      // eslint-disable-next-line no-magic-numbers
      return this.hls.levels.length > 1;
   }

   getAudioTracks() {
      return this.hls.audioTracks;
   }

   getAudioTrack() {
      return this.hls.audioTrack;
   }

   getTextTracks() {
      return this.$el.textTracks || [];
   }

   setAudioTrack(idx) {
      this.hls.audioTrack = idx;
   }

   destroy() {
      this.hls.destroy();
      super.destroy();
   }
}
